'use client';

import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { FSmeta } from '@sola/ark-skippy/mocks/mocks.js';
import { Breadcrumb } from '@sola/ark-skippy/molecules/breadcrumb/breadcrumb.js';
import { ArticleList } from '@sola/ark-skippy/organisms-cms/article-list/article-list.js';
import { BasicContentSolo } from '@sola/ark-skippy/organisms-cms/basic-content/solo/basic-content-solo.js';
import { FlexibleSection } from '@sola/ark-skippy/organisms/flexible-section/flexible-section.js';
import { keys } from '@sola/contentful-model/keys.js';
import {
  getCategoryTaxonomy,
  getTermCategory,
} from '@sola/contentful-model/lib/article.js';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.js';
import {
  ArticleCountriesEnum,
  ArticleThemesEnum,
  ArticleTopicsEnum,
  ArticleTypesEnum,
} from '@sola/contentful-model/lib/taxonomy-values.js';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.js';
import { camelCase, slugify } from '@sola/utils/string.ts';

const TermPageInner = ({
  type,
  term,
  locale: _locale,
}: {
  type?: ArticleTypesEnum;
  term?: ArticleThemesEnum | ArticleTopicsEnum | ArticleCountriesEnum;
  locale: LOCALES_CODES;
}) => {
  const { t } = useTranslation();
  const category = getTermCategory(term);
  const taxonomy = category ? getCategoryTaxonomy(category) : undefined;
  const isCountry = ArticleCountriesEnum.safeParse(term).success;

  let termName = t(
    `skippy.taxonomy.${taxonomy}.${term}.name`,
    t(`skippy.taxonomy.all`),
  );
  if (isCountry) {
    const country = camelCase(term);
    termName = t(`geo.seo.countries.${country}.name`);
  }

  const breadcrumbs = [
    {
      title: t(`skippy.home`, 'Home'),
      link: `/`,
    },
  ];

  if (type === keys.articleTypes.pressRelease) {
    breadcrumbs.push({
      title: t(`skippy.pressCenter`, 'Press Center'),
      link: articlesUrlBase[type].split('/').slice(0, -1).join('/'),
    });
  }

  breadcrumbs.push(
    ...(type
      ? [
          {
            title: t(`skippy.taxonomy.articleTypes.${type}.pluralName`) || '',
            link: articlesUrlBase[type],
          },
        ]
      : []),
  );
  breadcrumbs.push({
    title: termName || '',
    link: `${type ? articlesUrlBase[type] : ''}/tag/${slugify(term)}`,
  });

  let title = t(`skippy.taxonomy.all`);
  let seoDescription = term ? `"${term}" is not a valid tag` : '';

  if (category) {
    title = t(`skippy.taxonomy.${taxonomy}.${term}.name`);
    seoDescription = t(`skippy.taxonomy.${taxonomy}.${term}.seoDescription`);
  }
  if (isCountry) {
    const country = camelCase(term);
    title = t(`geo.seo.countries.${country}.name`);
    seoDescription = t(`geo.seo.countries.${country}.seoDescription`);
  }

  // @TODO: add SEO TITLE to the header

  return (
    <>
      <header>
        <Breadcrumb breadcrumbs={breadcrumbs} />
      </header>
      <main
        className="flex min-h-screen flex-col items-center p-24 bg-white"
        id="main"
      >
        <div className="w-full">
          <FlexibleSection
            meta={{ ...FSmeta, anchor: 'intro' }}
            children1={
              <>
                <BasicContentSolo
                  subtitle={t(`skippy.taxonomy.tagged`)}
                  title={title}
                  body={<p>{seoDescription}</p>}
                />
                <ArticleList articleType={type} term={term} />
              </>
            }
          />
        </div>
      </main>
    </>
  );
};

const TermPageSkeleton = () => (
  <>
    <header>
      <div className="flex items-center gap-comfy-sm py-comfy-md">
        <Skeleton height={16} width={40} />
        <Skeleton height={16} width={16} />
        <Skeleton height={16} width={80} />
        <Skeleton height={16} width={16} />
        <Skeleton height={16} width={120} />
      </div>
    </header>
    <main
      className="flex min-h-screen flex-col items-center p-24 bg-white"
      id="main"
    >
      <div className="w-full">
        <section className="component">
          <div className="container">
            <div className="text-center mb-comfy-xl">
              <Skeleton
                height={18}
                width={200}
                className="mx-auto mb-comfy-sm"
              />
              <Skeleton
                height={48}
                width={400}
                className="mx-auto mb-comfy-md"
              />
              <div className="max-w-2xl mx-auto">
                <div className="flex flex-col gap-snug-xs">
                  <Skeleton height={16} width="100%" />
                  <Skeleton height={16} width="90%" />
                  <Skeleton height={16} width="85%" />
                </div>
              </div>
            </div>

            <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="flex flex-col gap-comfy-sm">
                  <Skeleton
                    width="100%"
                    height="auto"
                    style={{ aspectRatio: '16/9' }}
                    borderRadius="0.25rem"
                  />
                  <Skeleton height={20} width="45%" />
                  <div className="flex flex-col gap-snug-xs">
                    <Skeleton height={28} width="100%" />
                    <Skeleton height={28} width="85%" />
                  </div>
                  <div className="flex flex-col gap-snug-xs">
                    <Skeleton height={20} width="100%" />
                    <Skeleton height={20} width="95%" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-comfy-lg pt-comfy-lg border-t border-neutral-mild">
              <div className="flex gap-snug-sm">
                <Skeleton height={48} width={48} borderRadius="0.5rem" />
                <Skeleton height={48} width={48} borderRadius="0.5rem" />
                <Skeleton height={48} width={48} borderRadius="0.5rem" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </>
);

export const TermPage = ({
  type,
  term,
  locale,
}: {
  type?: ArticleTypesEnum;
  term?: ArticleThemesEnum | ArticleTopicsEnum | ArticleCountriesEnum;
  locale: LOCALES_CODES;
}) => {
  return (
    <Suspense fallback={<TermPageSkeleton />}>
      <TermPageInner
        type={type}
        term={Array.isArray(term) ? term[0] : term}
        locale={locale}
      />
    </Suspense>
  );
};
